/**
 * pv-date.js
 *
 * Datele de piață și valorile implicite ale calculatorului.
 * SEPARAT de logică, exact ca să poți actualiza prețurile fără să atingi formulele.
 *
 * RITUAL DE ACTUALIZARE (trimestrial, sau când se schimbă tarifele ANRE):
 *   1. schimbi valorile de mai jos
 *   2. actualizezi ACTUALIZAT_LA
 *   3. rulezi:  node tools/build-ssr.mjs
 *   4. dai push pe tema nepublicată și verifici cu curl
 *
 * Pasul 3 e obligatoriu. Fără el, tabelul din HTML rămâne pe cifrele vechi.
 *
 * Legendă încredere:
 *   [OFICIAL]  sursă primară (ANRE, OPCOM, Eurostat, act normativ)
 *   [PIATA]    ofertă publicată de furnizor sau preț de piață observat
 *   [REPER]    estimare rezonabilă, de rafinat cu oferte reale
 *   [VERIFICA] valoare incertă sau contestată, nu o prezenta ca fapt
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory();
  } else {
    root.PVDate = factory();
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var ACTUALIZAT_LA = 'iulie 2026';

  var DEFAULTS = {
    // ---------- CONSUM ----------
    // [OFICIAL] Media reală pe loc de consum casnic e ~1.490 kWh/an, dar e trasă
    // în jos de apartamente. Pentru o casă care ar instala panouri, 5.000 e realist.
    // Praguri: apartament 1.800-3.000, casă fără încălzire electrică 3.000-5.000,
    // casă cu pompă de căldură și mașină electrică 9.000-14.000.
    consumAnual: 5000,

    // ---------- PREȚURI ENERGIE ----------
    // [OFICIAL] Preț mediu real all-in. ANRE martie 2026 (1,11587 lei/kWh fără
    // taxe și TVA) plus taxe și TVA 21% => ~1,46. Eurostat 2025-S2 = 1,4696.
    pretAllIn: 1.45,

    // [PIATA] Componenta de energie activă din contract. ASTA valorifică surplusul
    // injectat, nu prețul all-in. Interval real iulie 2026, Muntenia Sud:
    // 0,45363 (Hidroelectrica Viitor Hidro) ... 1,02602 (Engie Serviciu Universal).
    // 0,60 e mijloc de piață.
    componentaEnergie: 0.60,

    // [OFICIAL] Componente reglementate, valabile de la 01.07.2026, fără TVA.
    transportTL: 0.03645,
    serviciiSistem: 0.01470,
    // Distribuție JT pe zone: 0,31739 Muntenia Sud / Banat / Dobrogea,
    // 0,33325 Oltenia, 0,35534 Transilvania N+S și Muntenia Nord, 0,38791 Moldova.
    // ZONA TREBUIE SA FIE INPUT, nu presupunere. Tarifele s-au schimbat la
    // 01.07.2026 și nu în aceeași direcție în toate zonele.
    distributieJT: 0.31739,
    // Certificate verzi 0,07402 + cogenerare 0,01450 + acciză 0,00768 + CfD 0,000144
    taxeCertificateAcciza: 0.09634,
    // [OFICIAL] TVA standard pe energie, 21% din 1 august 2025.
    tva: 21,

    // [REPER] Creșterea anuală a prețului energiei. Ipoteză, nu prognoză.
    // Sensibilitatea rezultatului la parametrul asta e mare, deci trebuie editabil
    // și trebuie spus în pagină că e ipoteză.
    cresterePret: 3,

    // [REPER] Rata de actualizare pentru amortizarea actualizată,
    // adică costul de oportunitate al banilor.
    rataActualizare: 5,

    orizont: 25,

    // ---------- SISTEM FOTOVOLTAIC ----------
    kWp: 5,
    // [VERIFICA] Sistem 5 kWp fără baterie, montat, cu invertor.
    //
    // ATENTIE, cea mai slabă cifră din tot setul, și cea la care rezultatul e
    // cel mai sensibil. Sursele publice se contrazic:
    //  - reperul „la cheie" găsit (5 kW PV + 10 kWh baterie = 22.000-32.000 lei)
    //    ar implica ~2.400 lei/kWp pentru partea de panouri, dar acel total
    //    presupune aproape sigur o baterie de gamă buget (~630 lei/kWh), nu una
    //    de gamă medie (~1.650 lei/kWh)
    //  - piața cere de regulă 4.000-6.000 lei/kWp montat pentru sisteme mici
    //
    // Am ales 4.000 lei/kWp, adică mijlocul intervalului de piață. Nu amesteca
    // reperul „la cheie" cu prețul de baterie de gamă medie, dă rezultate
    // nerealist de optimiste (amortizare în 3 ani, ceea ce nu se întâmplă).
    //
    // DE INLOCUIT cu 2-3 oferte reale înainte de publicare.
    costSistemPV: 20000,
    // [OFICIAL] Sursa recomandată pentru producția specifică: PVGIS (Comisia
    // Europeană). 1.250 kWh/kWp/an e reper pentru sud, orientare bună.
    // [VERIFICA] pe zone înainte de publicare.
    productieSpecifica: 1250,
    // [REPER] Degradare panouri, standard de garanție.
    degradarePanouri: 0.5,
    // [REPER] Mentenanță anuală (curățare, verificări).
    mentenantaAnuala: 300,
    subventie: 0, // Casa Verde Fotovoltaice NU e finanțată în 2026. Vezi nota de jos.

    // ---------- AUTOCONSUM ----------
    // [REPER] Variabila care decide amortizarea. Fără baterie, o gospodărie
    // consumă direct tipic 25-40% din producție. Cu baterie, 60-80%.
    autoconsumFaraBaterie: 30,
    autoconsumCuBaterie: 70,

    // ---------- BATERIE ----------
    capacitateBaterie: 10,
    // [PIATA] 10 kWh mediu = 16.500 lei doar bateria (greenlead, 2026-05-02).
    // Montaj 3.000-8.000 și invertor hibrid de la ~4.000 sunt separate.
    // Valoarea de aici include o parte din montaj, ca să rămână în reperul
    // verificat de sistem la cheie.
    costBaterie: 16500,
    randamentBaterie: 90,
    anInlocuireBaterie: 12,
    // Procent din costul inițial, la înlocuire.
    costInlocuireBaterie: 70,

    // ---------- TARIF INDEXAT PZU ----------
    // [OFICIAL] Media ponderată PZU, OPCOM, primul semestru 2026: ~605 lei/MWh.
    // Lunar 2026: ian 790,41 / feb 519,04 / mar 556,70 / apr 514,65 / mai 565,05 / iun 694,48.
    pzuMediu: 0.605,
    // [VERIFICA] Componenta fixă de furnizare la PPC MaxControl. Paginile PPC
    // se contrazic: pagina de oferte PZU dă 149 lei/MWh, pagina MaxControl dă
    // 0,069 lei/kWh. Folosim 0,069 dar TREBUIE confirmat.
    pzuComponentaFixa: 0.069,
    // [OFICIAL] Plafon și prag PPC MaxControl pentru energia injectată.
    pzuPlafon: 0.999,
    pzuPragMin: 0,
    // [OFICIAL] Prețuri PZU observate pe intervale, iulie 2026.
    // Aici e miezul: un sistem FARA baterie injectează la amiază, exact în golul
    // de preț. 22 iulie: 0,2499 la 14:00-15:00 vs 1,0987 la 20:00-21:00.
    // 24 iulie: 0,0696 la 14:00-15:00 vs 1,0511 la 21:00-22:00.
    pzuAmiaza: 0.25,
    pzuSeara: 1.00,
    // [REPER] Cât din export poți muta spre vârful de seară, cu baterie.
    cotaExportMutatSeara: 60,
    // [REPER] Spread captabil pe arbitraj, pe kWh.
    // ATENTIE: spread-ul wholesale de 0,70-1,00 lei/kWh NU e captabil integral.
    // Pe retail, tarifele de rețea și TVA se aplică oricum pe consum.
    // Valoare conservatoare intenționat.
    spreadCaptabil: 0.35,
    zileArbitraj: 300,

    // ---------- YELLOWGRID ----------
    // [PIATA] Prag garantat la export, 0,50 lei/kWh.
    vppPretExportMin: 0.50,
    // [VERIFICA] Preț maxim la import, 0,55 lei/kWh. Interpretat în model ca
    // COMPONENTA DE ENERGIE, nu ca preț all-in, fiindcă podeaua reglementată
    // singură e 0,56-0,65 lei/kWh. DE CONFIRMAT cu YellowGrid, schimbă mult rezultatul.
    vppPretImportMax: 0.55,
    // [PIATA] Preț de export realizat la nivel de flotă, prima lună (sept. 2025): 0,71.
    vppPretExportRealizat: 0.71,
    // [PIATA] Cota clientului din upside-ul de peste prag. 75% client, 25% ei.
    vppCotaClient: 75,
    // [VERIFICA] Venit anual din servicii de rețea. NU EXISTA nicio estimare
    // independentă pe gospodărie. Singura cifră publicată e a lor,
    // "200-400 lei/lună în lunile bune", cu două hedge-uri.
    // IMPLICIT 0, intenționat. Nu inventa un venit anual.
    venitVPPAnual: 0
  };

  // Zonele de distribuție, ca input în interfață.
  var ZONE = [
    { id: 'muntenia_sud', nume: 'Muntenia Sud, Banat, Dobrogea', distributieJT: 0.31739 },
    { id: 'oltenia', nume: 'Oltenia', distributieJT: 0.33325 },
    { id: 'transilvania', nume: 'Transilvania Nord și Sud, Muntenia Nord', distributieJT: 0.35534 },
    { id: 'moldova', nume: 'Moldova', distributieJT: 0.38791 }
  ];

  // Profiluri rapide de consum, ca scurtături în interfață.
  var PROFILURI = [
    { id: 'apartament', nume: 'Apartament, 2 persoane', consumAnual: 2400 },
    { id: 'casa', nume: 'Casă, fără încălzire electrică', consumAnual: 5000 },
    { id: 'casa_pompa', nume: 'Casă cu pompă de căldură', consumAnual: 9000 },
    { id: 'casa_pompa_ev', nume: 'Casă cu pompă de căldură și mașină electrică', consumAnual: 12000 }
  ];

  var NOTE = {
    subventie: 'Casa Verde Fotovoltaice nu este finanțată în 2026. Pentru baterii există 400 milioane lei alocați, doar pentru prosumatori existenți, dar Ghidul Solicitantului nu este publicat și valoarea pe beneficiar nu este oficial cunoscută. Cifrele care circulă online sunt speculații.',
    contorInteligent: 'Tarifele dinamice cer contor inteligent integrat în SMI. Fără citire pe intervale de 15 minute, furnizorul aplică un profil standard de consum, deci pierzi tot beneficiul mutării consumului și bateria nu îți aduce nimic pe arbitraj.',
    injectie: 'Compensarea cantitativă valorifică energia injectată doar la componenta de energie activă din contract. Nu compensează transportul, distribuția, certificatele, acciza sau TVA. De aceea un prosumator ajuns la zero pe energie primește în continuare o factură reală.',
    vpp: 'YellowGrid este furnizor licențiat, nu un supliment peste furnizorul actual. Ca să intri, îți muți contractul de furnizare. Venitul din servicii de rețea este implicit 0 în calculator, fiindcă nu există nicio estimare independentă pe gospodărie.',
    arbitraj: 'Spread-ul mare dintre amiază și seară este pe piața wholesale. Pe retail se captează mult mai puțin, fiindcă tarifele de rețea și TVA se aplică oricum pe consum.'
  };

  return {
    ACTUALIZAT_LA: ACTUALIZAT_LA,
    DEFAULTS: DEFAULTS,
    ZONE: ZONE,
    PROFILURI: PROFILURI,
    NOTE: NOTE
  };
});
